import { copy } from "../copy";
import { platformOf, metricsPlatformId } from "../domain/accounts";
import { sourceLinkList } from "../domain/tasks";
import { topicAudience, topicTitle } from "../domain/display";
import { dayDelta, publishedThisWeek, scoreLabel, topByRank } from "../domain/flow";
import { topicsForAccount } from "../domain/selectors";
import { todayInShanghai } from "../domain/time";
import { decideTopics, dueToday, reviewTopics } from "../domain/worklist";
import { useApp } from "../state/store";
import type { Topic } from "../types";
import { FindDrawer } from "../components/FindDrawer";
import { PhoneReviewDrawer } from "./ReviewDesk";

export function Home() {
  const app = useApp();
  const params = new URLSearchParams(app.href.split("?")[1] ?? "");
  const today = todayInShanghai();
  const topics = topicsForAccount(app.state.topics, app.accountId);
  const pending = topByRank(decideTopics(topics), decideTopics(topics).length);
  const review = reviewTopics(topics);
  const due = dueToday(topics, today);
  const platform = metricsPlatformId(platformOf(app.state.accounts ?? [], app.accountId)) ?? "xiaohongshu";
  const delta = dayDelta(app.state.snapshots, platform, today);
  const week = publishedThisWeek(topics, today);
  const openTopic = review.find((topic) => topic.id === params.get("topic")) ?? null;

  function decide(topic: Topic, status: "已确认" | "不做") {
    const previous = topic.status;
    app.setStatus(topic.id, status);
    const message = status === "已确认" ? copy.status.confirmed(topicTitle(topic)) : copy.status.skipped(topicTitle(topic));
    app.notify(message, "info", { label: copy.undo, undo: { topicId: topic.id, status: previous } });
  }

  function finish(topic: Topic) {
    const previous = topic.status;
    app.setStatus(topic.id, "已完成");
    app.notify(`「${topicTitle(topic)}」标为已完成`, "info", { label: copy.undo, undo: { topicId: topic.id, status: previous } });
  }

  return (
    <div className="stack">
      <div className="stat-strip">
        <Stat label={copy.home.likes} value={delta.likes} />
        <Stat label={copy.home.saves} value={delta.saves} />
        <Stat label={copy.home.followers} value={delta.followers} />
        <div className="delta-item">
          <span>{copy.home.week}</span>
          <b className="num">{week}</b>
        </div>
        <button type="button" className="button primary" onClick={() => app.navigate("today", "find=1")}>{copy.home.find}</button>
      </div>

      {pending.length === 0 && review.length === 0 && due.length === 0 ? (
        <section className="panel">
          <p>{copy.home.empty}</p>
        </section>
      ) : (
        <>
          <section className="panel todo-block" aria-label={copy.home.decide}>
            <div className="panel-head">
              <h2>{copy.home.decide}</h2>
              <span className="panel-head-meta">
                <button type="button" className="text-action" onClick={() => app.navigate("inbox", "status=待确认")}>{copy.home.seeAll}</button>
                <b className="count-pill">{pending.length}</b>
              </span>
            </div>
            {pending.length === 0 ? <p className="muted">{copy.home.emptyDecide}</p> : (
              <div className="todo-list">
                {pending.map((topic) => (
                  <DecideRow key={topic.id} topic={topic} onDecide={decide} />
                ))}
              </div>
            )}
          </section>
          <section className="panel todo-block" aria-label={copy.home.review}>
            <div className="panel-head">
              <h2>{copy.home.review}</h2>
              <span className="panel-head-meta">
                <button type="button" className="text-action" onClick={() => app.navigate("review")}>{copy.home.goReview}</button>
                <b className="count-pill">{review.length}</b>
              </span>
            </div>
            {review.length === 0 ? <p className="muted">{copy.home.emptyReview}</p> : (
              <div className="todo-list">
                {review.map((topic) => (
                  <button
                    key={topic.id}
                    type="button"
                    className="todo-row as-link"
                    onClick={() => app.navigate("today", `topic=${encodeURIComponent(topic.id)}`)}
                  >
                    <span className="todo-copy"><b>{topicTitle(topic)}</b></span>
                    <span className="muted">{scoreLabel(app.state.postScores ?? [], topic.id)}</span>
                  </button>
                ))}
              </div>
            )}
          </section>
          <section className="panel todo-block" aria-label={copy.today.due}>
            <div className="panel-head">
              <h2>{copy.today.due}</h2>
              <b className="count-pill">{due.length}</b>
            </div>
            {due.length === 0 ? <p className="muted">{copy.today.emptyDue}</p> : (
              <div className="todo-list">
                {due.map((topic) => (
                  <div key={topic.id} className="todo-row">
                    <span className="todo-copy"><b>{topicTitle(topic)}</b></span>
                    <span className="row-actions">
                      <button type="button" className="button ghost" onClick={() => app.openTopic(topic.id)}>{copy.today.open}</button>
                      <button type="button" className="button line" onClick={() => finish(topic)}>{copy.today.finish}</button>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
      {params.get("find") === "1" && !openTopic ? <FindDrawer /> : null}
      {openTopic ? <PhoneReviewDrawer topic={openTopic} /> : null}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | null }) {
  const text = value == null ? copy.home.unsynced : value > 0 ? `+${value}` : String(value);
  return (
    <div className="delta-item">
      <span>{label}</span>
      <b className={value == null ? "muted" : "num"}>{text}</b>
    </div>
  );
}

function DecideRow({ topic, onDecide }: { topic: Topic; onDecide: (topic: Topic, status: "已确认" | "不做") => void }) {
  const links = sourceLinkList(topic);
  const detail = [topic.reason, topic.viralReason, topic.sources, ...links].some((item) => item && item.trim());
  return (
    <details className="decide-row">
      <summary>
        <span className="todo-copy">
          <b>{topicTitle(topic)}</b>
          <span>{[topic.angle.trim(), topicAudience(topic)].filter(Boolean).join(" · ")}</span>
        </span>
        <span className="row-actions">
          <button type="button" className="button line" onClick={(event) => { event.preventDefault(); event.stopPropagation(); onDecide(topic, "已确认"); }}>{copy.doIt}</button>
          <button type="button" className="button ghost" onClick={(event) => { event.preventDefault(); event.stopPropagation(); onDecide(topic, "不做"); }}>{copy.skip}</button>
        </span>
      </summary>
      {detail ? (
        <div className="decide-detail">
          {topic.reason?.trim() ? <p><span>{copy.home.reason}</span>{topic.reason.trim()}</p> : null}
          {topic.viralReason?.trim() ? <p><span>{copy.home.viral}</span>{topic.viralReason.trim()}</p> : null}
          {topic.sources?.trim() || links.length ? (
            <p>
              <span>{copy.home.sources}</span>
              {topic.sources?.trim() ? topic.sources.trim() : null}
              {links.map((link) => (
                <a key={link} href={link} target="_blank" rel="noreferrer">{link}</a>
              ))}
            </p>
          ) : null}
        </div>
      ) : <p className="muted decide-detail">{copy.home.noDetail}</p>}
    </details>
  );
}
